/**
 * hierarchical-navigation.ts - Hierarchical Navigation Profile
 * 
 * CHANGE HISTORY:
 * - 251116_2349 danielbeer: Create universal hierarchical navigation profile
 * 
 * Universal navigation pattern for hierarchical/tree structures.
 * Applicable to: Outliners, File Browsers, Menus, Tree Views, etc.
 * 
 * Provides semantic actions that can be overridden per context:
 * - action:primary (Enter) - Context-dependent primary action
 * - action:secondary (Space) - Context-dependent secondary action
 * - action:cancel (Escape) - Cancel/back action
 * - nav:up/down/left/right - Directional navigation
 * - nav:home/end - Jump to first/last
 * - nav:page-up/page-down - Page navigation
 */

import type { CommandProfile } from './types';

export const HIERARCHICAL_NAVIGATION_PROFILE: CommandProfile = {
  id: 'hierarchical-navigation',
  name: 'Hierarchical Navigation',
  description: 'Universal navigation pattern for tree/list structures',
  version: '1.0.0',
  
  commands: [
    // ========================================
    // DIRECTIONAL NAVIGATION
    // ========================================
    {
      id: 'nav:up',
      action: 'nav:up',
      keys: ['up'],
      label: 'Navigate Up',
      description: 'Navigate to previous item',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'navigation'
    },
    {
      id: 'nav:down',
      action: 'nav:down',
      keys: ['down'],
      label: 'Navigate Down',
      description: 'Navigate to next item',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'navigation'
    },
    {
      id: 'nav:left',
      action: 'nav:left',
      keys: ['left'],
      label: 'Navigate Left',
      description: 'Navigate to parent / collapse',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'navigation'
    },
    {
      id: 'nav:right',
      action: 'nav:right',
      keys: ['right'],
      label: 'Navigate Right',
      description: 'Navigate to child / expand',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'navigation'
    },
    
    // ========================================
    // JUMP NAVIGATION
    // ========================================
    {
      id: 'nav:home',
      action: 'nav:home',
      keys: ['home'],
      label: 'Jump to First',
      description: 'Navigate to first visible item',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'navigation'
    },
    {
      id: 'nav:end',
      action: 'nav:end',
      keys: ['end'],
      label: 'Jump to Last',
      description: 'Navigate to last visible item',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'navigation'
    },
    {
      id: 'nav:page-up',
      action: 'nav:page-up',
      keys: ['pageup'],
      label: 'Page Up',
      description: 'Navigate up one page',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'navigation'
    },
    {
      id: 'nav:page-down',
      action: 'nav:page-down',
      keys: ['pagedown'],
      label: 'Page Down',
      description: 'Navigate down one page',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'navigation'
    },
    
    // ========================================
    // SEMANTIC ACTIONS (Context-Dependent)
    // ========================================
    {
      id: 'action:primary',
      action: 'action:primary',
      keys: ['enter'],
      label: 'Primary Action',
      description: 'Context-dependent primary action',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'actions'
    },
    {
      id: 'action:secondary',
      action: 'action:secondary',
      keys: ['space'],
      label: 'Secondary Action',
      description: 'Context-dependent secondary action',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'actions'
    },
    {
      id: 'action:cancel',
      action: 'action:cancel',
      keys: ['escape'],
      label: 'Cancel',
      description: 'Cancel current action / go back',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: true,
      category: 'actions'
    },
    
    // ========================================
    // SELECTION
    // ========================================
    {
      id: 'select:extend-up',
      action: 'select:extend-up',
      keys: ['shift+up'],
      label: 'Extend Selection Up',
      description: 'Extend selection to previous item',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'selection'
    },
    {
      id: 'select:extend-down',
      action: 'select:extend-down',
      keys: ['shift+down'],
      label: 'Extend Selection Down',
      description: 'Extend selection to next item',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'selection'
    },
    {
      id: 'select:all',
      action: 'select:all',
      keys: ['cmd+a', 'ctrl+a'],
      label: 'Select All',
      description: 'Select all visible items',
      scope: 'navigation',
      preventDefault: true,
      allowInInput: false,
      category: 'selection'
    }
  ],
  
  // ========================================
  // CONTEXT OVERRIDES
  // ========================================
  contexts: {
    // Outliner context
    'outliner': [
      {
        action: 'action:primary',
        label: 'Add Sibling / Edit',
        description: 'Add new sibling after current item or start editing',
        handler: 'handlePrimaryAction'
      },
      {
        action: 'action:secondary',
        label: 'Toggle Expand/Collapse',
        description: 'Toggle expand/collapse of current item',
        handler: 'handleToggleExpand'
      },
      {
        action: 'action:cancel',
        label: 'Cancel Edit / Delete Empty',
        description: 'Cancel editing (delete if empty)',
        handler: 'handleCancelEdit'
      }
    ],
    
    // Menu context
    'menu': [
      {
        action: 'action:primary',
        label: 'Select Item',
        description: 'Select/activate the current menu item',
        handler: 'handleSelect'
      },
      {
        action: 'action:secondary',
        label: 'Select Item',
        description: 'Select/activate the current menu item (same as Enter)',
        handler: 'handleSelect'
      },
      {
        action: 'action:cancel',
        label: 'Close Menu',
        description: 'Close the current menu',
        handler: 'handleClose'
      },
      {
        action: 'nav:left',
        label: 'Close Submenu',
        description: 'Close submenu / go to parent menu',
        handler: 'handleCloseSubmenu'
      },
      {
        action: 'nav:right',
        label: 'Open Submenu',
        description: 'Open submenu if available',
        handler: 'handleOpenSubmenu'
      }
    ],
    
    // File browser context
    'file-browser': [
      {
        action: 'action:primary',
        label: 'Open',
        description: 'Open file or enter directory',
        handler: 'handleOpen'
      },
      {
        action: 'action:secondary',
        label: 'Quick Preview',
        description: 'Show quick preview of file',
        handler: 'handlePreview'
      },
      {
        action: 'action:cancel',
        label: 'Go Up',
        description: 'Go to parent directory',
        handler: 'handleGoUp'
      }
    ],
    
    // Tree view context (generic)
    'tree-view': [
      {
        action: 'action:primary',
        label: 'Select',
        description: 'Select the current item',
        handler: 'handleSelect'
      },
      {
        action: 'action:secondary',
        label: 'Toggle Expand',
        description: 'Toggle expand/collapse',
        handler: 'handleToggleExpand'
      }
    ]
  },
  
  metadata: {
    author: 'OpCentral',
    created: '2025-11-16',
    lastUpdated: '2025-11-16',
    tags: ['navigation', 'hierarchical', 'tree', 'universal']
  }
};
